import React, { useState, useMemo } from 'react';
import { X, Plus, Trash2, RefreshCw, AlertCircle, CheckCircle, XCircle, ArrowRightLeft } from 'lucide-react';
import {
    stcBinGrpRelationService,
    StcBinGrpRelationDto,
    CreateStcBinGrpRelationRequest
} from '../../../../../services/stcBinGrpRelationService';
import { StcBinGrpDto } from '../../../../../services/stcBinGrpService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface BinGrpRelationSidePanelProps {
    selectedMasterNo: number;
    data: StcBinGrpRelationDto[];
    binGroups: StcBinGrpDto[];
    onClose: () => void;
    onSave: () => void;
}

export const BinGrpRelationSidePanel: React.FC<BinGrpRelationSidePanelProps> = ({
    selectedMasterNo,
    data,
    binGroups,
    onClose,
    onSave
}) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'sub' | 'slett'>('sub');

    // Inline edit state
    const [editingRelationId, setEditingRelationId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState<number>(0);

    // Add-new state
    const [addingNew, setAddingNew] = useState(false);
    const [newChildNo, setNewChildNo] = useState<number>(0);

    // Busy / feedback
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // Delete-all confirm
    const [deleteAllConfirmed, setDeleteAllConfirmed] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // ── Derived data ──────────────────────────────────────────────────────────
    const masterGroup = useMemo(
        () => binGroups.find(g => g.binGrpNo === selectedMasterNo),
        [binGroups, selectedMasterNo]
    );

    const childRelations = useMemo(
        () => data.filter(r => r.masterBinGrpNo === selectedMasterNo),
        [data, selectedMasterNo]
    );

    const usedChildNos = useMemo(
        () => childRelations.map(r => r.childBinGrpNo),
        [childRelations]
    );

    // Alle unike master-gruppenumre (disse kan ikke velges som sub)
    const allMasterNos = useMemo(
        () => data.map(r => r.masterBinGrpNo).filter((v, i, arr) => arr.indexOf(v) === i),
        [data]
    );

    const availableForNew = useMemo(
        () => binGroups.filter(
            g => g.binGrpNo !== selectedMasterNo
              && !usedChildNos.includes(g.binGrpNo)
              && !allMasterNos.includes(g.binGrpNo)
        ),
        [binGroups, selectedMasterNo, usedChildNos, allMasterNos]
    );

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getBinGrpLabel = (no: number) => {
        const g = binGroups.find(g => g.binGrpNo === no);
        return g ? `${no} - ${g.description1}` : String(no);
    };

    const showSuccess = (msg: string) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(''), 2000);
    };

    const resetError = () => setError('');

    // ── Edit single child ─────────────────────────────────────────────────────
    const handleStartEdit = (rel: StcBinGrpRelationDto) => {
        setEditingRelationId(rel.id);
        setEditValue(rel.childBinGrpNo);
        resetError();
        setAddingNew(false);
    };

    const handleCancelEdit = () => {
        setEditingRelationId(null);
        resetError();
    };

    const handleSaveEdit = async (rel: StcBinGrpRelationDto) => {
        if (editValue === rel.childBinGrpNo) { setEditingRelationId(null); return; }
        if (editValue === 0) { setError(t('manager.modules.adminBins.binGrpRelation.errors.selectSub', {}, 'Velg en sub-gruppe')); return; }
        if (editValue === selectedMasterNo) { setError(t('manager.modules.adminBins.binGrpRelation.errors.subSameAsMasterShort', {}, 'Sub kan ikke være samme som master')); return; }
        if (usedChildNos.filter(n => n !== rel.childBinGrpNo).includes(editValue)) {
            setError(t('manager.modules.adminBins.binGrpRelation.errors.subAlreadyAddedShort', {}, 'Sub-gruppen er allerede lagt til')); return;
        }
        if (allMasterNos.includes(editValue)) {
            setError(t('manager.modules.adminBins.binGrpRelation.errors.groupIsMaster', {}, 'Denne gruppen er allerede registrert som Master og kan ikke være Sub')); return;
        }
        setBusy(true);
        resetError();
        try {
            await stcBinGrpRelationService.delete(rel.companyNo, rel.masterBinGrpNo, rel.childBinGrpNo);
            const req: CreateStcBinGrpRelationRequest = {
                companyNo: rel.companyNo,
                masterBinGrpNo: rel.masterBinGrpNo,
                childBinGrpNo: editValue,
            };
            await stcBinGrpRelationService.create(req);
            setEditingRelationId(null);
            showSuccess(t('manager.modules.adminBins.binGrpRelation.subSwapped', {}, 'Sub-gruppe byttet'));
            onSave();
        } catch (e: any) {
            setError(e.message || t('manager.modules.adminBins.binGrpRelation.errors.changeFailed', {}, 'Feil ved endring'));
        } finally {
            setBusy(false);
        }
    };

    // ── Delete single child ───────────────────────────────────────────────────
    const handleDeleteChild = async (rel: StcBinGrpRelationDto) => {
        if (!window.confirm(t('manager.modules.adminBins.binGrpRelation.confirmDeleteRelationTo', { '0': getBinGrpLabel(rel.childBinGrpNo) }, `Slette relasjonen til ${getBinGrpLabel(rel.childBinGrpNo)}?`))) return;
        setBusy(true);
        resetError();
        try {
            await stcBinGrpRelationService.delete(rel.companyNo, rel.masterBinGrpNo, rel.childBinGrpNo);
            showSuccess(t('manager.modules.adminBins.binGrpRelation.relationDeleted', {}, 'Relasjon slettet'));
            onSave();
        } catch (e: any) {
            setError(e.message || t('manager.modules.adminBins.common.errors.deleteFailed', {}, 'Feil ved sletting'));
        } finally {
            setBusy(false);
        }
    };

    // ── Add new child ─────────────────────────────────────────────────────────
    const handleAddNew = async () => {
        if (newChildNo === 0) { setError(t('manager.modules.adminBins.binGrpRelation.errors.selectSub', {}, 'Velg en sub-gruppe')); return; }
        if (newChildNo === selectedMasterNo) { setError(t('manager.modules.adminBins.binGrpRelation.errors.subSameAsMasterShort', {}, 'Sub kan ikke være samme som master')); return; }
        if (usedChildNos.includes(newChildNo)) { setError(t('manager.modules.adminBins.binGrpRelation.errors.subAlreadyAddedShort', {}, 'Sub-gruppen er allerede lagt til')); return; }
        if (allMasterNos.includes(newChildNo)) { setError(t('manager.modules.adminBins.binGrpRelation.errors.groupIsMaster', {}, 'Denne gruppen er allerede registrert som Master og kan ikke være Sub')); return; }
        setBusy(true);
        resetError();
        try {
            const companyNo = childRelations[0]?.companyNo || masterGroup?.companyNo || '';
            const req: CreateStcBinGrpRelationRequest = {
                companyNo,
                masterBinGrpNo: selectedMasterNo,
                childBinGrpNo: newChildNo,
            };
            await stcBinGrpRelationService.create(req);
            setAddingNew(false);
            setNewChildNo(0);
            showSuccess(t('manager.modules.adminBins.binGrpRelation.subAdded', {}, 'Sub-gruppe lagt til'));
            onSave();
        } catch (e: any) {
            setError(e.message || t('manager.modules.adminBins.common.errors.createFailedShort', {}, 'Feil ved opprettelse'));
        } finally {
            setBusy(false);
        }
    };

    // ── Delete ALL relations for this master ──────────────────────────────────
    const handleDeleteAll = async () => {
        if (childRelations.length === 0) return;
        setIsDeleting(true);
        resetError();
        try {
            await Promise.all(
                childRelations.map(r =>
                    stcBinGrpRelationService.delete(r.companyNo, r.masterBinGrpNo, r.childBinGrpNo)
                )
            );
            showSuccess(t('manager.modules.adminBins.binGrpRelation.allRelationsDeleted', {}, 'Alle relasjoner for master slettet'));
            onSave();
            onClose();
        } catch (e: any) {
            setError(e.message || t('manager.modules.adminBins.common.errors.deleteFailed', {}, 'Feil ved sletting'));
        } finally {
            setIsDeleting(false);
            setDeleteAllConfirmed(false);
        }
    };

    // ── Available sub options for the edit dropdown of a specific row ─────────
    const getAvailableForEdit = (currentChildNo: number) =>
        binGroups.filter(
            g =>
                g.binGrpNo !== selectedMasterNo &&
                (g.binGrpNo === currentChildNo || !usedChildNos.includes(g.binGrpNo)) &&
                (g.binGrpNo === currentChildNo || !allMasterNos.includes(g.binGrpNo))
        );

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-25 transition-opacity" onClick={onClose} />

            <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">
                            {t('manager.modules.adminBins.binGrpRelation.maintainMaster', { '0': selectedMasterNo }, `Vedlikehold – Master ${selectedMasterNo}`)}
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {masterGroup?.description1 || ''}
                            {masterGroup?.commonAvailable && (
                                <span className="ml-2 inline-flex items-center gap-0.5 text-green-700">
                                    <CheckCircle className="w-3 h-3" /> {t('manager.modules.adminBins.binGrpRelation.commonGroup', {}, 'Fellesgruppe')}
                                </span>
                            )}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => { setActiveTab('sub'); resetError(); }}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'sub'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        {t('manager.modules.adminBins.binGrpRelation.subGroupsTab', { '0': childRelations.length }, `Sub-grupper (${childRelations.length})`)}
                    </button>
                    <button
                        onClick={() => { setActiveTab('slett'); resetError(); setDeleteAllConfirmed(false); }}
                        className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === 'slett'
                                ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        {t('manager.modules.adminBins.binGrpRelation.deleteMasterTab', {}, 'Slett master')}
                    </button>
                </div>

                {/* Feedback bar */}
                {(error || successMsg) && (
                    <div className={`px-4 py-2 text-xs flex items-center gap-2 ${
                        error
                            ? 'bg-red-50 border-b border-red-200 text-red-700'
                            : 'bg-green-50 border-b border-green-200 text-green-700'
                    }`}>
                        {error
                            ? <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                            : <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />}
                        {error || successMsg}
                        {error && (
                            <button onClick={resetError} className="ml-auto text-red-400 hover:text-red-600">
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    {activeTab === 'sub' ? (
                        /* ── Sub-grupper fane ──────────────────────────────── */
                        <div className="p-4 space-y-1">
                            {childRelations.length === 0 && !addingNew && (
                                <p className="text-sm text-gray-500 italic py-2">
                                    {t('manager.modules.adminBins.binGrpRelation.noSubGroups', {}, 'Ingen sub-grupper registrert for denne master-gruppen.')}
                                </p>
                            )}

                            {/* Sub-gruppe-rader */}
                            {childRelations.map(rel => (
                                <div
                                    key={rel.id}
                                    className="border border-gray-200 rounded-lg bg-white overflow-hidden"
                                >
                                    {editingRelationId === rel.id ? (
                                        /* Edit mode */
                                        <div className="p-3 space-y-2 bg-blue-50 border-blue-200">
                                            <div className="flex items-center gap-1.5 text-xs font-medium text-blue-700">
                                                <ArrowRightLeft className="w-3.5 h-3.5" />
                                                {t('manager.modules.adminBins.binGrpRelation.swapSubGroup', {}, 'Bytt sub-gruppe')}
                                            </div>
                                            <select
                                                value={editValue}
                                                onChange={e => setEditValue(parseInt(e.target.value) || 0)}
                                                className="w-full px-2 py-1.5 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 bg-white"
                                                autoFocus
                                            >
                                                <option value={0}>{t('manager.modules.adminBins.binGrpRelation.selectPlaceholder', {}, 'Velg...')}</option>
                                                {getAvailableForEdit(rel.childBinGrpNo).map(g => (
                                                    <option key={g.id} value={g.binGrpNo}>
                                                        {g.binGrpNo} - {g.description1}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleSaveEdit(rel)}
                                                    disabled={busy || editValue === 0}
                                                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                                >
                                                    {busy
                                                        ? <RefreshCw className="w-3 h-3 animate-spin" />
                                                        : t('common.buttons.save', {}, 'Lagre')}
                                                </button>
                                                <button
                                                    onClick={handleCancelEdit}
                                                    className="flex-1 px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                                                >
                                                    {t('common.buttons.cancel', {}, 'Avbryt')}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        /* View mode */
                                        <div className="flex items-center gap-2 px-3 py-2">
                                            <div className="flex-1 min-w-0">
                                                <span className="font-mono text-sm text-gray-800 font-medium">{rel.childBinGrpNo}</span>
                                                <span className="text-xs text-gray-500 ml-2 truncate">
                                                    {binGroups.find(g => g.binGrpNo === rel.childBinGrpNo)?.description1 || ''}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => handleStartEdit(rel)}
                                                disabled={busy}
                                                title={t('manager.modules.adminBins.binGrpRelation.changeSubGroup', {}, 'Endre sub-gruppe')}
                                                className="flex-shrink-0 px-2 py-1 text-xs text-gray-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                                            >
                                                {t('manager.modules.adminBins.binGrpRelation.change', {}, 'Endre')}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteChild(rel)}
                                                disabled={busy}
                                                title={t('manager.modules.adminBins.binGrpRelation.deleteThisRelation', {}, 'Slett denne relasjonen')}
                                                className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Legg til ny sub */}
                            {addingNew ? (
                                <div className="border border-blue-300 rounded-lg bg-blue-50 p-3 space-y-2 mt-2">
                                    <div className="flex items-center gap-1.5 text-xs font-medium text-blue-700">
                                        <Plus className="w-3.5 h-3.5" />
                                        {t('manager.modules.adminBins.binGrpRelation.newSubGroup', {}, 'Ny sub-gruppe')}
                                    </div>
                                    <select
                                        value={newChildNo}
                                        onChange={e => setNewChildNo(parseInt(e.target.value) || 0)}
                                        className="w-full px-2 py-1.5 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 bg-white"
                                        autoFocus
                                    >
                                        <option value={0}>{t('manager.modules.adminBins.binGrpRelation.selectSubGroup', {}, 'Velg sub-gruppe...')}</option>
                                        {availableForNew.map(g => (
                                            <option key={g.id} value={g.binGrpNo}>
                                                {g.binGrpNo} - {g.description1}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleAddNew}
                                            disabled={busy || newChildNo === 0}
                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                                        >
                                            {busy
                                                ? <RefreshCw className="w-3 h-3 animate-spin" />
                                                : <><Plus className="w-3 h-3" /> {t('common.buttons.add', {}, 'Legg til')}</>}
                                        </button>
                                        <button
                                            onClick={() => { setAddingNew(false); setNewChildNo(0); resetError(); }}
                                            className="flex-1 px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                                        >
                                            {t('common.buttons.cancel', {}, 'Avbryt')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => { setAddingNew(true); setEditingRelationId(null); resetError(); }}
                                    disabled={busy || availableForNew.length === 0}
                                    className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed transition-colors"
                                >
                                    <Plus className="w-4 h-4" />
                                    {availableForNew.length === 0
                                        ? t('manager.modules.adminBins.binGrpRelation.noAvailableSubGroups', {}, 'Ingen tilgjengelige sub-grupper')
                                        : t('manager.modules.adminBins.binGrpRelation.addSubGroup', {}, 'Legg til sub-gruppe')}
                                </button>
                            )}
                        </div>
                    ) : (
                        /* ── Slett master fane ─────────────────────────────── */
                        <div className="p-4 space-y-4">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-red-800">
                                        <p className="font-medium">{t('manager.modules.adminBins.binGrpRelation.deleteAllForMaster', { '0': selectedMasterNo }, `Slett alle relasjoner for master ${selectedMasterNo}`)}</p>
                                        <p className="mt-1 text-xs">
                                            {t('manager.modules.adminBins.binGrpRelation.deleteAllWarning', { '0': childRelations.length }, `Dette vil slette samtlige ${childRelations.length} sub-gruppe-relasjoner knyttet til denne master-gruppen. Handlingen kan ikke angres.`)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Summary of what will be deleted */}
                            <div className="space-y-1">
                                <p className="text-xs font-medium text-gray-600 mb-1">
                                    {t('manager.modules.adminBins.binGrpRelation.relationsToDelete', { '0': childRelations.length }, `Relasjoner som slettes (${childRelations.length}):`)}
                                </p>
                                <div className="max-h-52 overflow-y-auto space-y-1">
                                    {childRelations.map(rel => (
                                        <div
                                            key={rel.id}
                                            className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700"
                                        >
                                            <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                                            <span className="font-mono">{selectedMasterNo}</span>
                                            <span className="text-gray-400">→</span>
                                            <span className="font-mono">{rel.childBinGrpNo}</span>
                                            <span className="text-gray-500 truncate">
                                                {binGroups.find(g => g.binGrpNo === rel.childBinGrpNo)?.description1 || ''}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Confirmation checkbox */}
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={deleteAllConfirmed}
                                    onChange={e => setDeleteAllConfirmed(e.target.checked)}
                                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                />
                                <span className="text-sm text-gray-700">
                                    {t('manager.modules.adminBins.binGrpRelation.confirmDeleteAllCheckbox', { '0': childRelations.length }, `Jeg bekrefter at jeg vil slette alle ${childRelations.length} relasjoner`)}
                                </span>
                            </label>

                            <button
                                onClick={handleDeleteAll}
                                disabled={!deleteAllConfirmed || isDeleting || childRelations.length === 0}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                                    !deleteAllConfirmed || isDeleting || childRelations.length === 0
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                }`}
                            >
                                {isDeleting ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        {t('manager.modules.adminBins.common.deleting', {}, 'Sletter...')}
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        {t('manager.modules.adminBins.binGrpRelation.deleteAllRelations', {}, 'Slett alle relasjoner')}
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        {t('common.buttons.close', {}, 'Lukk')}
                    </button>
                </div>
            </div>
        </div>
    );
};
